import { useEffect, useMemo, useState } from "react";

const INSTALL_BANNER_SNOOZE_KEY = "lytick:pwa-install-banner-snooze-until";
const INSTALL_VISIT_COUNT_KEY = "lytick:pwa-install-visit-count";
const INSTALL_LAST_SEEN_KEY = "lytick:pwa-install-last-seen";
const INSTALL_BANNER_SNOOZE_MS = 3 * 24 * 60 * 60 * 1000;
const INSTALL_VISIT_SESSION_GAP_MS = 12 * 60 * 60 * 1000;
const INSTALL_DWELL_MS = 45 * 1000;

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed"; platform: string }>;
}

function isStandaloneDisplayMode() {
  if (typeof window === "undefined") return false;

  const standaloneMatch =
    typeof window.matchMedia === "function" ? window.matchMedia("(display-mode: standalone)").matches : false;

  return standaloneMatch || (window.navigator as Navigator & { standalone?: boolean }).standalone === true;
}

function readNumberFromStorage(key: string): number {
  if (typeof window === "undefined") return 0;

  try {
    const rawValue = window.localStorage.getItem(key);
    const parsedValue = Number(rawValue);
    return Number.isFinite(parsedValue) ? parsedValue : 0;
  } catch {
    return 0;
  }
}

function writeNumberToStorage(key: string, value: number) {
  if (typeof window === "undefined") return;

  try {
    window.localStorage.setItem(key, String(value));
  } catch {}
}

function removeFromStorage(key: string) {
  if (typeof window === "undefined") return;

  try {
    window.localStorage.removeItem(key);
  } catch {}
}

function normalizeFutureTimestamp(value: number): number {
  return Number.isFinite(value) && value > Date.now() ? value : 0;
}

function isLocalDevelopmentHost() {
  if (typeof window === "undefined") {
    return false;
  }

  return ["localhost", "127.0.0.1"].includes(window.location.hostname);
}

function recordVisit() {
  const now = Date.now();
  const previousSeenAt = readNumberFromStorage(INSTALL_LAST_SEEN_KEY);
  const previousVisitCount = readNumberFromStorage(INSTALL_VISIT_COUNT_KEY);
  const isFreshVisit = now - previousSeenAt > INSTALL_VISIT_SESSION_GAP_MS;
  const nextVisitCount = isFreshVisit ? previousVisitCount + 1 : Math.max(previousVisitCount, 1);

  writeNumberToStorage(INSTALL_VISIT_COUNT_KEY, nextVisitCount);
  writeNumberToStorage(INSTALL_LAST_SEEN_KEY, now);

  return nextVisitCount;
}

export function usePwaInstall({ hasMeaningfulEngagement = false } = {}) {
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [isInstalled, setIsInstalled] = useState(isStandaloneDisplayMode);
  const [snoozeUntil, setSnoozeUntil] = useState(() =>
    normalizeFutureTimestamp(readNumberFromStorage(INSTALL_BANNER_SNOOZE_KEY))
  );
  const [visitCount, setVisitCount] = useState(() => Math.max(readNumberFromStorage(INSTALL_VISIT_COUNT_KEY), 1));
  const [hasMetDwellTime, setHasMetDwellTime] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined") return;

    const visitTimeoutId = window.setTimeout(() => {
      setVisitCount(recordVisit());
    }, 0);

    const displayModeQuery =
      typeof window.matchMedia === "function" ? window.matchMedia("(display-mode: standalone)") : null;

    const syncInstalledState = () => {
      const installed = isStandaloneDisplayMode();
      setIsInstalled(installed);

      if (installed) {
        setDeferredPrompt(null);
        removeFromStorage(INSTALL_BANNER_SNOOZE_KEY);
        setSnoozeUntil(0);
      }
    };

    const handleBeforeInstallPrompt = (event: Event) => {
      const promptEvent = event as BeforeInstallPromptEvent;
      syncInstalledState();

      if (isStandaloneDisplayMode()) return;
      if (isLocalDevelopmentHost()) return;

      promptEvent.preventDefault();

      setDeferredPrompt(promptEvent);
      setSnoozeUntil(normalizeFutureTimestamp(readNumberFromStorage(INSTALL_BANNER_SNOOZE_KEY)));
    };

    const handleAppInstalled = () => {
      setIsInstalled(true);
      setDeferredPrompt(null);
      removeFromStorage(INSTALL_BANNER_SNOOZE_KEY);
      setSnoozeUntil(0);
    };

    syncInstalledState();

    window.addEventListener("beforeinstallprompt", handleBeforeInstallPrompt);
    window.addEventListener("appinstalled", handleAppInstalled);
    displayModeQuery?.addEventListener("change", syncInstalledState);

    return () => {
      window.clearTimeout(visitTimeoutId);
      window.removeEventListener("beforeinstallprompt", handleBeforeInstallPrompt);
      window.removeEventListener("appinstalled", handleAppInstalled);
      displayModeQuery?.removeEventListener("change", syncInstalledState);
    };
  }, []);

  useEffect(() => {
    if (typeof window === "undefined" || snoozeUntil <= 0) return;

    const remainingMs = Math.max(0, snoozeUntil - Date.now());
    const timeoutId = window.setTimeout(() => {
      removeFromStorage(INSTALL_BANNER_SNOOZE_KEY);
      setSnoozeUntil(0);
    }, remainingMs);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [snoozeUntil]);

  useEffect(() => {
    if (typeof window === "undefined" || isInstalled) return;

    const dwellTimeoutId = window.setTimeout(() => {
      setHasMetDwellTime(true);
    }, INSTALL_DWELL_MS);

    return () => {
      window.clearTimeout(dwellTimeoutId);
    };
  }, [isInstalled]);

  const dismissPromotion = () => {
    const nextSnoozeUntil = Date.now() + INSTALL_BANNER_SNOOZE_MS;
    writeNumberToStorage(INSTALL_BANNER_SNOOZE_KEY, nextSnoozeUntil);
    setSnoozeUntil(nextSnoozeUntil);
  };

  const promptInstall = async (): Promise<boolean> => {
    if (!deferredPrompt || typeof deferredPrompt.prompt !== "function") {
      return false;
    }

    try {
      await deferredPrompt.prompt();
      const choiceResult = await deferredPrompt.userChoice;
      const accepted = choiceResult?.outcome === "accepted";

      if (accepted) {
        removeFromStorage(INSTALL_BANNER_SNOOZE_KEY);
        setSnoozeUntil(0);
      } else {
        dismissPromotion();
      }

      setDeferredPrompt(null);
      return accepted;
    } catch {
      return false;
    }
  };

  const isPromotionMature = hasMeaningfulEngagement || (visitCount >= 2 && hasMetDwellTime);
  const canInstall = !isInstalled && Boolean(deferredPrompt);
  const showInstallBanner = !isInstalled && !snoozeUntil && isPromotionMature && canInstall;

  const installLabel = useMemo(() => {
    if (isInstalled) return "Installed";
    if (deferredPrompt) return "Install App";
    return "Install Unavailable";
  }, [deferredPrompt, isInstalled]);

  return {
    canInstall,
    dismissPromotion,
    installLabel,
    isInstalled,
    promptInstall,
    showInstallBanner,
  };
}
